import { useCallback, useMemo, useState } from 'react';

import { timezones } from '@/config/constants';
import { DASHBOARD_PREFIX, DASHBOARD_DEFAULT_BASE_NAME } from '@/entities/dashboard';
import type { Option } from '@/entities/user-config';
import { updateUserConfig } from '@/entities/user-config';
import { useSaveUserConfig, useUserConfig, useUserDetails } from '@/features/_shared/user';
import { useDashboard } from '@/features/dashboard';
import { useMarketsList } from '@/features/markets/api/marketsList.options';
import { clock } from '@/services/clock';
import useSnackbarNotifications from '@/shared/hooks/useSnackbarNotifications';

const usePreferences = () => {
  const { data: userConfigData } = useUserConfig();
  const { userEmail } = useUserDetails();
  const saveUserConfigMutation = useSaveUserConfig(userEmail ?? '');
  const timezone = userConfigData?.timezone ?? { label: 'UTC', value: 'UTC' };
  const dateFormat = userConfigData?.dateFormat ?? { label: '14-Mar-25', value: 'dd-MMM-yy' };
  const userDefaultMarket = userConfigData?.defaultMarket ?? { label: '', value: '' };
  const userHomepage = userConfigData?.homepage ?? { label: '', value: '' };
  const { data: marketsData } = useMarketsList();
  const groupedMarkets = marketsData?.groupedMarkets ?? [];
  const marketsList = marketsData?.marketsList ?? [];
  const { data: dashboards } = useDashboard();
  const { showLoadingSnackbar, showErrorSnackbar, showSuccessSnackbar } =
    useSnackbarNotifications();
  const [saving, setSaving] = useState(false);

  const timezoneOptions = useMemo(() => {
    const now = clock.date();
    return timezones.map(tz => ({
      label: tz,
      value: tz,
      info: `(${new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour: 'numeric',
        minute: '2-digit',
        hour12: false
      }).format(now)})`
    }));
  }, []);

  const marketsOptions = useMemo(
    () =>
      groupedMarkets.map(group => {
        return {
          label: group.name,
          options: group.markets.map(market => ({
            label: market.name,
            value: market.marketId as string
          }))
        };
      }),
    [groupedMarkets]
  );

  const dashboardOptions = useMemo<Option[]>(() => {
    if (!dashboards) {
      return [];
    }

    return Object.keys(dashboards).map(name => ({
      label: name,
      value: DASHBOARD_PREFIX + name
    }));
  }, [dashboards]);

  const getFallbackMarketOption = useCallback(
    (userOption: Option): Option => {
      if (userOption.value.length) {
        return userOption;
      }
      const firstAvailable = marketsList[0];
      if (!firstAvailable) {
        return {
          label: '',
          value: ''
        };
      }
      return {
        label: firstAvailable.metadata.displayName,
        value: firstAvailable.marketId
      };
    },
    [marketsList]
  );

  const defaultMarket = useMemo(
    () => getFallbackMarketOption(userDefaultMarket),
    [getFallbackMarketOption, userDefaultMarket]
  );

  const homepageOptions = useMemo(
    () => [
      ...(dashboardOptions.length
        ? [
            {
              label: 'Dashboards',
              options: dashboardOptions
            }
          ]
        : [{ label: DASHBOARD_DEFAULT_BASE_NAME, value: `my-dashboard` }]),
      ...marketsOptions
    ],
    [dashboardOptions, marketsOptions]
  );

  const homepage = useMemo(() => {
    if (userHomepage.value.length) {
      return userHomepage;
    }

    if (dashboardOptions.length) {
      return dashboardOptions[0];
    }

    return { label: DASHBOARD_DEFAULT_BASE_NAME, value: 'my-dashboard' };
  }, [dashboardOptions, userHomepage]);

  const changeUserSetting = useCallback(
    (
      settingName: 'timezone' | 'dateFormat' | 'defaultMarket' | 'homepage',
      userSetting: Option,
      showSnackbar = true
    ) => {
      if (!userConfigData) return;
      const config = updateUserConfig(userConfigData, { [settingName]: userSetting });
      const key = `preferences-${settingName}`;
      if (showSnackbar) showLoadingSnackbar('Saving your changes, please wait...', key);
      setSaving(true);
      saveUserConfigMutation
        .mutateAsync(config)
        .then(() => {
          if (showSnackbar) showSuccessSnackbar('Changes saved!', key);
        })
        .catch(() => {
          showErrorSnackbar(
            "Oops! We couldn't save your changes. Please try again.",
            undefined,
            key
          );
        })
        .finally(() => {
          setSaving(false);
        });
    },
    [
      saveUserConfigMutation,
      showErrorSnackbar,
      showLoadingSnackbar,
      showSuccessSnackbar,
      userConfigData
    ]
  );

  return {
    timezone,
    dateFormat,
    defaultMarket,
    changeUserSetting,
    timezoneOptions,
    marketsOptions,
    homepage,
    homepageOptions,
    saving
  };
};

export default usePreferences;
